import React, { useEffect, useState } from "react";
import UserPageLayout from "../../../../components/UserPageLayout";
import { useAppContext } from "../../../../../../context/AppContext";
import CardLoader from "../../../../../../components/CardLoader";
import Pagination from "../../../../../../components/Pagination";
import { highlightText } from "../../../../../../utils/highlight";
import { LogsData } from "../../../../network/api/LogsData";

const pickFirst = (source, keys, fallback = null) => {
    for (const key of keys) {
        const value = source?.[key];
        if (value !== undefined && value !== null && value !== "") {
            return value;
        }
    }

    return fallback;
};

const truncateText = (value, maxLength = 80) => {
    if (!value) {
        return "-";
    }

    const text = String(value);
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

const formatDateTime = (value) => {
    if (!value) {
        return "-";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString();
};

const formatMetadataSummary = (metadata) => {
    if (!metadata) {
        return "";
    }

    if (typeof metadata === "string") {
        return truncateText(metadata, 90);
    }

    if (typeof metadata !== "object") {
        return String(metadata);
    }

    const preferredKeys = ["ip_address", "ip", "route", "path", "field", "reason", "module"];
    const summary = preferredKeys
        .filter((key) => metadata[key] !== undefined && metadata[key] !== null && metadata[key] !== "")
        .map((key) => `${key}: ${metadata[key]}`)
        .join(" | ");

    if (summary) {
        return truncateText(summary, 90);
    }

    return truncateText(JSON.stringify(metadata), 90);
};

const normalizeLog = (item) => {
    const actor = item?.user || item?.admin || item?.actor || {};
    const target = item?.target_data || item?.target || {};
    const metadata = item?.metadata || item?.details || item?.context || null;

    return {
        id: pickFirst(item, ["id"]),
        actorName: pickFirst(actor, ["fullname", "displayname", "display_name", "email", "username"], "System"),
        actorEmail: pickFirst(actor, ["email"]),
        targetLabel: pickFirst(
            target,
            ["title", "name", "displayname", "display_name", "key", "k_ey", "slug", "email"],
            pickFirst(item, ["target_model", "target_type", "target"], "System")
        ),
        targetType: pickFirst(item, ["target_model", "target_type"]),
        message: pickFirst(item, ["message", "description"], "-"),
        status: pickFirst(item, ["status"], "-"),
        actionType: pickFirst(item, ["action_type", "action"], "-"),
        createdAt: pickFirst(item, ["created_at", "updated_at", "timestamp"]),
        metadataSummary: formatMetadataSummary(metadata),
        raw: item,
    };
};

const getActionBadgeClass = (actionType) => {
    if (actionType === "create") return "badge-primary";
    if (actionType === "update") return "badge-warning";
    if (actionType === "delete") return "badge-danger";
    if (actionType === "login") return "badge-success";
    return "badge-secondary";
};

const getStatusBadgeClass = (status) => {
    const lowered = String(status || "").toLowerCase();
    if (["success", "active", "ok"].includes(lowered)) return "badge-success";
    if (["pending", "warning"].includes(lowered)) return "badge-warning";
    if (["error", "failed", "failure", "blocked"].includes(lowered)) return "badge-danger";
    return "badge-secondary";
};

export default function Logs() {
    const { setPageTitle, setPageUserID } = useAppContext();

    const [logList, setLogList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [logsReload, setLogsReload] = useState(false);

    const [pageProps, setPageProps] = useState({
        page: 1,
        totalPages: 1,
        totalEntries: 0,
        entryLimit: 0,
        visiblePages: []
    });

    const [pageFilters, setPageFilters] = useState({
        page: null,
        limit: 10,
        user: null,
        message: null,
    });

    const [searchValue, setSearchValue] = useState("");

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pageProps.totalPages) {
            setPageProps((prev) => ({
                ...prev,
                page,
            }));

            setPageFilters((prev) => ({
                ...prev,
                page,
            }));
        }
    };

    const handleLimitChange = (value) => {
        setPageFilters((prev) => ({
            ...prev,
            page: null,
            limit: value,
        }));
    };

    const handleSearchChange = (e) => {
        e.preventDefault();
        const { value } = e.target;
        setSearchValue(value);

        if (value.length >= 3) {
            setPageFilters((prev) => ({
                ...prev,
                page: null,
                user: value,
                message: value,
            }));
        }

        if (value.length === 0) {
            setPageFilters((prev) => ({
                ...prev,
                page: null,
                user: null,
                message: null,
            }));
        }
    };

    useEffect(() => {
        const fetchLogs = async () => {
            setIsLoading(true);
            setErrorMessage(null);

            try {
                const response = await LogsData({ pageFilters });

                if (response.status === "success") {
                    const logs = Array.isArray(response?.data?.data) ? response.data.data.map(normalizeLog) : [];
                    setLogList(logs);
                    setPageProps({
                        page: response?.data?.page || 1,
                        totalPages: response?.data?.total_pages || 1,
                        totalEntries: response?.data?.total_records || 0,
                        entryLimit: response?.data?.limit || pageFilters.limit || 10,
                        visiblePages: response?.data?.visible_pages || [1],
                    });
                } else {
                    setErrorMessage({ code: response.code, message: response.message });
                }
            } catch (err) {
                setErrorMessage({ code: "500", message: "Failed to load system logs." });
            } finally {
                setIsLoading(false);
            }
        };

        fetchLogs();
    }, [pageFilters, logsReload]);

    useEffect(() => {
        setPageUserID(null);
        setPageTitle("System Logs");
    }, [setPageTitle, setPageUserID]);

    return (
        <UserPageLayout>
            <div className="content-body">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card custom-card">
                                {(isLoading || errorMessage) && (
                                    <CardLoader
                                        errorMessage={errorMessage}
                                        onRetry={() => { setLogsReload(!logsReload); }}
                                    />
                                )}
                                <div className="card-header">
                                    <div className="d-flex justify-content-between align-items-center w-100">
                                        <h4 className="card-title">System Logs</h4>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div id="guestTable-all-1_wrapper" className="table-responsive dataTables_wrapper no-footer">
                                        <div className="dataTables_length" id="page_limit">
                                            <label>
                                                Show{" "}
                                                <select
                                                    name="page_limit"
                                                    aria-controls="example3"
                                                    className=""
                                                    value={pageProps.entryLimit}
                                                    onChange={(e) => handleLimitChange(Number(e.target.value))}
                                                >
                                                    <option value="10">10</option>
                                                    <option value="25">25</option>
                                                    <option value="50">50</option>
                                                    <option value="100">100</option>
                                                </select>{" "}
                                                entries
                                            </label>
                                        </div>
                                        <div id="example3_filter" className="dataTables_filter">
                                            <label>
                                                Search:
                                                <input
                                                    type="search"
                                                    className=""
                                                    placeholder=""
                                                    aria-controls="example3"
                                                    value={searchValue}
                                                    onChange={handleSearchChange}
                                                />
                                            </label>
                                        </div>
                                        <table className="table table-striped" style={{ minWidth: "1100px" }}>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    <th>Actor</th>
                                                    <th>Target</th>
                                                    <th>Message</th>
                                                    <th>Status</th>
                                                    <th>Action</th>
                                                    <th>Details</th>
                                                    <th>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {logList.length === 0 && !isLoading ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-5 text-muted">
                                                            No logs found
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    logList.map((item, index) => (
                                                        <tr key={item.id || index}>
                                                            <td>{(pageProps.entryLimit * (pageProps.page - 1)) + (index + 1)}</td>
                                                            <td>
                                                                <div className="fw-semibold">
                                                                    {highlightText(item.actorName, searchValue)}
                                                                </div>
                                                                <div className="small text-muted">
                                                                    {highlightText(item.actorEmail || "-", searchValue)}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="fw-semibold">
                                                                    {highlightText(item.targetLabel || "-", searchValue)}
                                                                </div>
                                                                <div className="small text-muted">
                                                                    {item.targetType || "-"}
                                                                </div>
                                                            </td>
                                                            <td title={item.message}>
                                                                {highlightText(truncateText(item.message, 70), searchValue)}
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${getStatusBadgeClass(item.status)}`}>
                                                                    {item.status}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${getActionBadgeClass(item.actionType)}`}>
                                                                    {item.actionType}
                                                                </span>
                                                            </td>
                                                            <td title={item.metadataSummary || "-"}>
                                                                {highlightText(item.metadataSummary || "-", searchValue)}
                                                            </td>
                                                            <td>{formatDateTime(item.createdAt)}</td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>

                                        <Pagination
                                            pageProps={pageProps}
                                            onPageChange={handlePageChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </UserPageLayout>
    );
}
